import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { OrganizationDataModel } from 'models/CompanySetup/organizationDataModel.model';
import { ParentOrganization } from 'models/Target/Target.model';
import { TargetCoverageMeta } from 'models/Target/targetCoverageMeta.model';
import { TargetYearsMeta } from 'models/Target/targetYearsmeta.model';
import { allowOnlyNumbersWithoutDots } from 'shared/utils/convertFormData';
import { generateYearOptions } from 'shared/utils/generateYearOptions';
import styles from './createTargetModal.module.scss';
interface TargetField {
  index: number;
  label: string;
  name: string;
  placeholder: string;
  isSelect?: boolean;
  options?: {
    label?: string | number;
    value?: string | number | boolean;
    disabled?: boolean;
  }[]; // adjust the type if you have more specific information about options
  listHeight?: number;
  isTextarea?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  helpdesk?: HelpDeskInputFieldEnum;
  className?: string;
}
const {
  targetName,
  isSME,
  selectTarget,
  selectOrganization,
  targetCoverage,
  targetSettingMethod,
  baselineYear,
  targetYear,
  notes,
  totalReduction,
} = HelpDeskInputFieldEnum;

// Common fields with index/index
const commonFields = (
  organizationData: OrganizationDataModel | null,
  targetCoverageMeta: TargetCoverageMeta[],
  targetYearsMeta: TargetYearsMeta[],
  isCustomTarget?: boolean,
  organization?: ParentOrganization
): TargetField[] => {
  return [
    {
      index: 1,
      label: 'Target Name',
      name: 'targetName',
      placeholder: 'Target Name',
      helpdesk: targetName,
    },
    {
      index: 4,
      label: 'Select Organization',
      name: 'organizationId',
      placeholder: 'Select Organization',
      isSelect: true,
      options: [
        {
          label: organization?.name ?? '',
          value: organization?.id ?? '',
        },
      ],
      disabled: true,
      helpdesk: selectOrganization,
    },
    {
      index: 5,
      label: 'Target Coverage',
      name: 'targetCoverage',
      placeholder: 'Target Coverage',
      isSelect: true,
      options: isCustomTarget
        ? [
          { label: 'Scope 1, 2', value: 'scope1_scope2' },
          { label: 'Scope 3 (Coming Soon)', value: 'scope3', disabled: true },
          {
            label: 'Scope 1, 2, 3 (Coming Soon)',
            value: 'scope1_scope2_scope3',
            disabled: true,
          },
        ]
        : [...targetCoverageMeta],
      helpdesk: targetCoverage,
    },
    {
      index: 6,
      label: 'Target Setting Method',
      name: 'targetSettingMethod',
      placeholder: 'Target Setting Method',
      isSelect: true,
      options: [
        {
          label: 'Absolute Contraction',
          value: 'absolute_contraction',
        },
      ],
      allowClear: false,
      helpdesk: targetSettingMethod,
    },
    {
      index: 7,
      label: 'Baseline Year',
      name: 'baselineYear',
      placeholder: 'Baseline Year',
      isSelect: true,
      options: [
        {
          label: organizationData?.company?.baselineYear?.toString() ?? '',
          value: organizationData?.company?.baselineYear?.toString() ?? '',
        },
      ],
      disabled: true,
      helpdesk: baselineYear,
    },
    {
      index: 8,
      label: 'Target Year',
      name: 'targetYear',
      placeholder: 'Target Year',
      isSelect: true,
      options: isCustomTarget
        ? generateYearOptions([2025, 2050])
        : [...targetYearsMeta],
      helpdesk: targetYear,
    },
    {
      index: 10,
      label: 'Notes',
      name: 'notes',
      placeholder: 'Notes',
      isTextarea: true,
      helpdesk: notes,
    },
  ];
};

// Unique fields for science-based targets with index/index
const scienceBasedUniqueFields: TargetField[] = [
  {
    index: 2,
    label: 'Are you an SME as per SBTi eligibility criteria?',
    name: 'isSME',
    placeholder: 'Are you an SME as per SBTi eligibility criteria?',
    isSelect: true,
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
    allowClear: false,
    helpdesk: isSME,
    className: styles.smeSelect,
  },
  {
    index: 3,
    label: 'Select Target',
    name: 'targetType',
    placeholder: 'Select Target',
    isSelect: true,
    options: [
      { label: 'Near Term', value: 'near_term' },
      { label: 'Net Zero', value: 'net_zero' },
    ],
    helpdesk: selectTarget,
  },
];

// Unique fields for custom targets with index
const customTargetUniqueFields: TargetField[] = [
  {
    index: 9,
    label: 'Total Reduction (%)',
    name: 'totalReduction',
    placeholder: 'Total Reduction (%)',
    onKeyDown: allowOnlyNumbersWithoutDots,
    helpdesk: totalReduction,
  },
];

// Function to merge fields and sort by index
const mergeFields = (
  uniqueFields: TargetField[],
  organizationData: OrganizationDataModel | null,
  targetCoverageMeta: TargetCoverageMeta[],
  targetYearsMeta: TargetYearsMeta[],
  isCustomTarget?: boolean,
  organization?: ParentOrganization
) => {
  const desiredCommonFields = commonFields(
    organizationData,
    targetCoverageMeta,
    targetYearsMeta,
    isCustomTarget,
    organization
  );
  return [...desiredCommonFields, ...uniqueFields].sort(
    (a, b) => a.index - b.index
  );
};

// Merged and ordered fields for science-based targets
export const scienceBasedFields = (
  organizationData: OrganizationDataModel | null,
  targetCoverageMeta: TargetCoverageMeta[],
  targetYearsMeta: TargetYearsMeta[],
  isCustomTarget?: boolean,
  organization?: ParentOrganization
) =>
  mergeFields(
    scienceBasedUniqueFields,
    organizationData,
    targetCoverageMeta,
    targetYearsMeta,
    isCustomTarget,
    organization
  );

// Merged and ordered fields for custom targets
export const customTargetFields = (
  organizationData: OrganizationDataModel | null,
  targetCoverageMeta: TargetCoverageMeta[],
  targetYearsMeta: TargetYearsMeta[],
  isCustomTarget: boolean,
  organization?: ParentOrganization
) =>
  mergeFields(
    customTargetUniqueFields,
    organizationData,
    targetCoverageMeta,
    targetYearsMeta,
    isCustomTarget,
    organization
  );
